import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { PanelGroup, Panel } from "react-resizable-panels";

import ResizeHandle from "components/common/ResizeHandle";
import MaximizeHost from "components/common/MaximizeHost";
import MaximizablePanel from "components/common/MaximizablePanel";
import AssetWellsSelector from "../components/AssetWellsSelector";
import DcaManagementWellsSelector from "./DcaManagementWellsSelector";
import DcaManagementWellsMap from "./components/DcaManagementWellsMap";
import DcaManagementForecast from "./components/DcaManagementForecast";
import DcaManagementHistoryWatch from "./components/DcaManagementHistoryWatch";
import AddProjectForm from "components/project/AddProjectForm";
import DcaManagementDataPanel from "./components/DcaManagementDataPanel";

import {
  setShowNewProjectPopup,
  addProjectItem,
  setActiveProjectId,
  selectActiveProject,
} from "features/project/projectsSlice";

import {
  getProjectDetail,
  parseProjectId,
  saveDcaManagementProjectDetail,
} from "service/projects";
import {
  reset,
  setActiveWell,
  setDcaWells,
  addDcaWell,
  setActiveDcaWell,
  selectDcaWell,
  selectDcaWells,
} from "features/decline_analysis/dcaManagementSlice";

import useLoading from "components/common/useLoading";
import { Spin } from "antd";

function DcaManagement(props) {
  let [searchParams] = useSearchParams();
  const projectId = parseProjectId(searchParams.get("project_id"));
  const projectType = searchParams.get("project_type");
  const projectName = searchParams.get("project_name");

  const activeProject = useSelector(selectActiveProject);
  const activeDcaWell = useSelector(selectDcaWell);
  const dcaWells = useSelector(selectDcaWells);

  const [projectLoadStatus, setProjectLoadStatus] = useState(null); // loading, loaded
  const [selectedWellIds, setSelectedWellIds] = useState(null);

  const [projectParamsToSave, setProjectParamsToSave] = useState(null);
  // UX-REWORK: the map is demoted to collapsible context (no longer a fixed
  // golden-row half). Closed by default so the Available Data panel gets the
  // width; toggled open into a vertical resizable map/data split.
  const [mapOpen, setMapOpen] = useState(false);
  // The DCA-project-wells navigator (2nd selector) is only useful once the
  // project actually has analyzed wells; when empty it collapses so the map /
  // Available Data panel reclaims that horizontal space.
  const hasDcaWells = Array.isArray(dcaWells) && dcaWells.length > 0;
  const [LoadingIcon, showLoadingIcon, hideLoadingIcon] = useLoading(
    <Spin size="large" />,
    60
  );

  const dispatch = useDispatch();

  const refreshData = useCallback(
    (projectId) => {
      showLoadingIcon();
      setProjectLoadStatus("loading");
      getProjectDetail(
        projectId,
        (result) => {
          setProjectLoadStatus("loaded");
          if (activeProject == null) {
            dispatch(
              addProjectItem({
                id: result.project_id,
                name: result.project_name,
                project_type: result.project_type,
                custom_data: result.custom_data,
              })
            );
            dispatch(setActiveProjectId(result.project_id));
          }
          if (result.wells) {
            dispatch(setDcaWells(result.wells));
          }

          hideLoadingIcon();
        },
        (error) => {
          console.error(error);
          setProjectLoadStatus(null);
          hideLoadingIcon();
        }
      );
    },
    [activeProject, dispatch, hideLoadingIcon, showLoadingIcon]
  );

  useEffect(() => {
    return () => {
      dispatch(reset());
      dispatch(setActiveProjectId());
    };
  }, [dispatch]);

  useEffect(() => {
    if (
      projectLoadStatus === "loading" ||
      projectLoadStatus === "loaded" ||
      (projectId == null && activeProject == null)
    )
      return;
    refreshData(projectId || activeProject.id);
    // eslint-disable-next-line
  }, [projectLoadStatus, projectId, activeProject, dispatch]);

  useEffect(() => {
    if (dcaWells == null || dcaWells.length === 0) {
      return;
    }
    if (activeDcaWell == null) {
      dispatch(setActiveDcaWell(dcaWells[0].well_uwi));
    }
  }, [dispatch, activeDcaWell, dcaWells]);

  const submitDcmProject = useCallback(
    (projId, projectParams) => {
      projectParams.project_id = projId;
      showLoadingIcon();
      saveDcaManagementProjectDetail(
        projectParams,
        (result) => {
          setProjectParamsToSave(null);
          if (result?.success !== true) {
            hideLoadingIcon();
            console.error("DCA save failed!");
            return;
          }

          dispatch(addDcaWell(result.wells));
          hideLoadingIcon();
        },
        (error) => {
          console.error("Save management setting failed!", error);
          hideLoadingIcon();
        }
      );
    },
    [dispatch, hideLoadingIcon, showLoadingIcon]
  );

  const handleSubmitDcmProject = useCallback(
    (projectParams) => {
      if (projectId === null && activeProject === null) {
        dispatch(setShowNewProjectPopup(true));
        setProjectParamsToSave(projectParams);
        return;
      }

      submitDcmProject(projectId || activeProject.id, projectParams);
    },
    [activeProject, projectId, dispatch, submitDcmProject]
  );

  const handleWellSelected = useCallback(
    (well) => {
      const wellId = well.uwi;
      // single select
      if (
        selectedWellIds == null ||
        selectedWellIds.includes(wellId) === false
      ) {
        setSelectedWellIds([wellId]);
        dispatch(setActiveWell(well));
        dispatch(setActiveDcaWell(well.uwi));
      }
    },
    [dispatch, selectedWellIds]
  );

  const handleDcaWellSelected = useCallback(
    (well) => {
      // single select
      if (
        selectedWellIds == null ||
        selectedWellIds.includes(well.uwi) === false
      ) {
        setSelectedWellIds([well.uwi]);
        dispatch(setActiveWell(well));
        dispatch(setActiveDcaWell(well.uwi));
      }
    },
    [dispatch, selectedWellIds]
  );

  return (
    <div>
      <AddProjectForm
        input={{
          project_type: "dca_management",
          project_name: `PRJ_${new Date().getTime()}`,
          comment: "comment",
        }}
        callbackFunction={[submitDcmProject, projectParamsToSave]}
        loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
      />
      <LoadingIcon />

      <div
        className="page-content d-flex flex-column hide-scrollbar"
        style={{ position: "relative" }}
      >
        {/* Window "maximize / restore" host — overlays the split layout and
            covers ONLY this .page-content (left nav + breadcrumb stay visible). */}
        <MaximizeHost>
          {/* UX-REWORK · user-resizable split layout (react-resizable-panels).
              Outer vertical group splits the demoted CONTEXT STRIP from the
              work region; autoSaveId persists every divider drag to
              localStorage. The old fixed .row-wrapper (45.1vh) rows are gone —
              the user sets the proportions by dragging. */}
          <PanelGroup
            direction="vertical"
            autoSaveId="dcm-v1"
            style={{ flexGrow: 1, minHeight: 0 }}
          >
            {/* CONTEXT STRIP — fixed-width selector(s) on the left; the map +
                Available Data panel fills the rest of the width. */}
            <Panel id="dcm-context" order={1} defaultSize={28} minSize={12}>
              <div className="d-flex flex-row h-100">
                {/* Selectors: fixed 142px each. The DCA-project-wells navigator
                    renders ONLY when the project has analyzed wells, so a fresh
                    project reclaims that space for the map / Available Data. */}
                <div
                  className="d-flex flex-row h-100"
                  style={{ flex: "0 0 auto" }}
                >
                  <div
                    className="well-selector pe-1 h-100"
                    style={{ flexShrink: 0 }}
                  >
                    <AssetWellsSelector
                      selectedWellIds={selectedWellIds}
                      onWellSelected={handleWellSelected}
                      loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
                      autoSelectFirst={!projectId}
                    />
                  </div>
                  {hasDcaWells && (
                    <div
                      className="well-selector pe-1 h-100"
                      style={{ flexShrink: 0 }}
                    >
                      <DcaManagementWellsSelector
                        selectedWellIds={selectedWellIds}
                        onWellSelected={handleDcaWellSelected}
                        loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
                      />
                    </div>
                  )}
                </div>

                {/* Map + Available Data: fills remaining width. When the map is
                    open, the two stack in a vertical resizable group so the user
                    drags the map's HEIGHT (dcm-context-map-v1 persists it); when
                    closed, Available Data fills the whole block. */}
                <div
                  className="d-flex flex-column flex-grow-1 ps-1"
                  style={{ minWidth: 0 }}
                >
                  <div
                    className="d-flex flex-row mb-1"
                    style={{ flex: "0 0 auto" }}
                  >
                    <span
                      className="btn-label header"
                      role="button"
                      onClick={() => setMapOpen((v) => !v)}
                      style={{ cursor: "pointer", marginRight: 8 }}
                      title="Show / hide the well-location map"
                    >
                      {mapOpen ? "▼ Map" : "▶ Map"}
                    </span>
                  </div>
                  {mapOpen ? (
                    <PanelGroup
                      direction="vertical"
                      autoSaveId="dcm-context-map-v1"
                      style={{ flexGrow: 1, minHeight: 0 }}
                    >
                      <Panel
                        id="dcm-map"
                        order={1}
                        defaultSize={50}
                        minSize={20}
                      >
                        <MaximizablePanel
                          id="dcm-map"
                          className="h-100 pb-1"
                          buttonStyle={{ right: 38 }}
                        >
                          <DcaManagementWellsMap
                            selectedWellIds={selectedWellIds}
                            onWellSelected={handleWellSelected}
                            loadingIconActions={[
                              showLoadingIcon,
                              hideLoadingIcon,
                            ]}
                          />
                        </MaximizablePanel>
                      </Panel>
                      <ResizeHandle direction="vertical" id="dcm-map-hh" />
                      <Panel
                        id="dcm-avail"
                        order={2}
                        defaultSize={50}
                        minSize={20}
                      >
                        <MaximizablePanel
                          id="dcm-avail"
                          className="h-100 pt-1"
                          buttonStyle={{ right: 44 }}
                        >
                          <DcaManagementDataPanel
                            selectedWellIds={selectedWellIds}
                            loadingIconActions={[
                              showLoadingIcon,
                              hideLoadingIcon,
                            ]}
                          />
                        </MaximizablePanel>
                      </Panel>
                    </PanelGroup>
                  ) : (
                    <MaximizablePanel
                      id="dcm-avail"
                      className="flex-grow-1"
                      buttonStyle={{ right: 44 }}
                    >
                      <DcaManagementDataPanel
                        selectedWellIds={selectedWellIds}
                        loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
                      />
                    </MaximizablePanel>
                  )}
                </div>
              </div>
            </Panel>

            <ResizeHandle direction="vertical" id="dcm-v-handle" />

            {/* WORK REGION — History Match | (draggable) | Forecast (Save). */}
            <Panel id="dcm-work" order={2} defaultSize={72} minSize={30}>
              <PanelGroup
                direction="horizontal"
                autoSaveId="dcm-work-h1"
                style={{ flexGrow: 1, minHeight: 0 }}
              >
                <Panel
                  id="dcm-history"
                  order={1}
                  defaultSize={50}
                  minSize={25}
                >
                  <MaximizablePanel
                    id="dcm-history"
                    className="h-100 pe-1"
                    buttonStyle={{ right: 38 }}
                  >
                    <DcaManagementHistoryWatch
                      // well_data={activeWell}
                      loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
                    />
                  </MaximizablePanel>
                </Panel>

                <ResizeHandle direction="horizontal" id="dcm-work-hh" />

                <Panel
                  id="dcm-forecast"
                  order={2}
                  defaultSize={50}
                  minSize={25}
                >
                  <MaximizablePanel
                    id="dcm-forecast"
                    className="h-100 ps-1"
                    buttonStyle={{ right: 38 }}
                  >
                    <DcaManagementForecast
                      submitDcaProject={handleSubmitDcmProject}
                      loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
                    />
                  </MaximizablePanel>
                </Panel>
              </PanelGroup>
            </Panel>
          </PanelGroup>
        </MaximizeHost>
      </div>
    </div>
  );
}

export default DcaManagement;
